<template>
  <div class="d-flex flex-column gap-2">
    <h3 class="d-flex flex-row gap-3 align-center py-2">
      <v-icon size="16" v-if="isUrgent" icon="mdi-flag" class="text--color-error-60" />

      <span class="typography-heading-lg-bold">Move</span>

      <TaskIssueChip
        v-if="isTaskInProgress && task.id"
        :task-id="task.id"
        type="move"
        :tags="taskTags"
        :on-issue-reported="handleIssueReported"
        :on-issue-cleared="handleIssueCleared" />
    </h3>

    <v-card variant="flat">
      <v-card-title class="text-center d-flex flex-row gap-2 align-center justify-center">
        <v-icon size="20" icon="mdi-truck-trailer"></v-icon>

        <span class="typography-heading-lg">
          {{ assetContainerCode }}
        </span>
      </v-card-title>
    </v-card>

    <!-- TODO: Shape cards like chevron -->
    <div
      class="d-flex position-relative gap-2 double-card"
      :class="{ 'in-progress': isTaskInProgress }">
      <v-card
        class="flex-fill chevron-card"
        variant="flat"
        :class="{
          'outlined-border': showOriginOutlinedBorder
        }"
        v-bind="originClickProps">
        <v-card-text class="py-6 px-4 d-flex flex-column gap-2 align-center justify-center">
          <span class="typography-button-sm d-flex flex-row gap-1 align-center">
            <v-icon
              v-if="showOriginCheckIcon"
              size="16"
              icon="mdi-check-circle"
              color="primary"></v-icon>
            PICK FROM:
          </span>

          <div v-if="hasOriginSpot" class="d-flex flex-column gap-1 align-center">
            <span class="typography-heading-md text-center text-wrap">
              {{ originSpotAreaName }}
            </span>
            <span class="typography-heading-xlg-bold text-center text-wrap">
              {{ originSpotCode }}
            </span>
          </div>
          <div v-else class="d-flex flex-column gap-1 align-center">
            <span v-if="isTaskCreated" class="typography-heading-md text-center">
              Start task to select
            </span>
            <div v-else class="d-flex flex-column justify-center align-center gap-1 mt-1">
              <span class="typography-heading-md-bold">Select...</span>
              <v-icon icon="mdi-gesture-tap" size="28"></v-icon>
            </div>
          </div>
        </v-card-text>
      </v-card>

      <v-card
        class="flex-fill chevron-card to"
        variant="flat"
        :class="{
          'outlined-border': showDestinationOutlinedBorder
        }"
        v-bind="destinationClickProps">
        <v-card-text class="py-6 px-4 d-flex flex-column gap-2 align-center justify-center">
          <span class="typography-button-sm d-flex flex-row gap-1 align-center">
            <v-icon
              v-if="showDestinationCheckIcon"
              size="16"
              icon="mdi-check-circle"
              color="primary"></v-icon>
            MOVE TO:
          </span>
          <div v-if="hasDestinationSpot" class="d-flex flex-column gap-1 align-center">
            <span class="typography-heading-md text-center text-wrap">
              {{ destinationSpotAreaName }}
            </span>
            <span class="typography-heading-xlg-bold text-center text-wrap">
              {{ destinationSpotCode }}
            </span>
          </div>
          <div v-else class="d-flex flex-column gap-1 align-center">
            <span v-if="isTaskCreated" class="typography-heading-md text-center">
              Start task to select
            </span>
            <div v-else class="d-flex flex-column justify-center align-center gap-1 mt-1">
              <span class="typography-heading-md-bold">Select...</span>
              <v-icon icon="mdi-gesture-tap" size="28"></v-icon>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </div>

    <v-card variant="flat">
      <v-card-text class="text-center">
        <task-meta :task="task" class="justify-center"></task-meta>
      </v-card-text>
    </v-card>

    <div class="d-flex position-relative gap-2 flex-column flex-md-row double-card">
      <v-card variant="flat" class="flex-fill">
        <v-card-text class="d-flex flex-column gap-2">
          <h4 class="typography-heading-md-bold">Task Description</h4>
          <span class="typography-body-md" style="white-space: pre-line">
            {{ taskDescription }}
          </span>
        </v-card-text>
      </v-card>

      <v-card variant="flat" class="flex-fill">
        <v-card-text class="d-flex flex-column gap-2">
          <h4 class="typography-heading-md-bold">Attachments</h4>
          <span class="typography-body-md">No attachments</span>
        </v-card-text>
      </v-card>
    </div>
  </div>
</template>

<script setup lang="ts">
// TODO: Component needs translations

import { computed, watch, toRef } from 'vue';
import { get } from 'lodash';

import { useYardTask, useTaskQueries, useDialogTrigger, useNotify } from '@/composables';
import { useYardTaskAnalytics } from '@/analytics/yardTaskEvents';
import { YardTaskStatus, type MoveTaskViewData, AssetContainerType } from '@nova/core';
import { isTaskBlocked } from '@satellite/helpers/yardTaskUtils';
import TaskMeta from '@/components/TaskMeta.vue';
import TaskIssueChip from '@/components/TaskIssueChip.vue';

import type { ISpot } from '@nova/core';

const props = defineProps<{
  task: MoveTaskViewData;
}>();

const emit = defineEmits<{
  (e: 'allow-complete', allow: boolean): void;
}>();

const { notify } = useNotify();
const selectSpotDialog = useDialogTrigger('open-select-spot-dialog');
const selectAssetDialog = useDialogTrigger('open-select-asset-dialog');

const task = toRef(props, 'task');

const { getAssetContainerCode, isUrgent, taskDescription, taskTags } = useYardTask({
  initialTask: task
});

const assetContainerCode = computed(() => getAssetContainerCode());
const { useUpdateTaskMutation } = useTaskQueries();
const updateTaskMutation = useUpdateTaskMutation();
const { trackYardTaskUpdatedOriginSpot, trackYardTaskUpdatedDestinationSpot } =
  useYardTaskAnalytics();

const isTaskInProgress = computed(() => {
  return props.task.status === YardTaskStatus.IN_PROGRESS;
});

const isTaskCreated = computed(() => {
  return props.task.status === YardTaskStatus.CREATED;
});

const hasOriginSpot = computed(() => {
  return Boolean(get(props.task, 'originSpot'));
});

const hasDestinationSpot = computed(() => {
  return Boolean(get(props.task, 'destinationSpot'));
});

const isBlocked = computed(() => isTaskBlocked(props.task));

const originSpotAreaName = computed(() => {
  return get(props.task, 'originSpotArea.name') || 'N/A';
});

const originSpotCode = computed(() => {
  return get(props.task, 'originSpot.code') || 'N/A';
});

const destinationSpotAreaName = computed(() => {
  return get(props.task, 'destinationSpotArea.name') || 'N/A';
});

const destinationSpotCode = computed(() => {
  return get(props.task, 'destinationSpot.code') || 'N/A';
});

const isOriginClickable = computed(() => {
  return isTaskInProgress.value;
});

const isDestinationClickable = computed(() => {
  return isTaskInProgress.value;
});

const showOriginCheckIcon = computed(() => {
  return isTaskInProgress.value && hasOriginSpot.value;
});

const showDestinationCheckIcon = computed(() => {
  return isTaskInProgress.value && hasDestinationSpot.value;
});

const showOriginOutlinedBorder = computed(() => {
  return !hasOriginSpot.value && isTaskInProgress.value;
});

const showDestinationOutlinedBorder = computed(() => {
  return !hasDestinationSpot.value && isTaskInProgress.value;
});

const assetContainerType = computed(() => {
  return (get(props.task, 'assetContainer.type') as AssetContainerType) || undefined;
});

const handleSpotSelected = (selectedSpot: ISpot) => {
  const taskId = props.task.id;
  if (!taskId) return;

  updateTaskMutation.mutate(
    {
      taskId,
      params: {
        child: {
          destinationSpotId: selectedSpot.id
        }
      }
    },
    {
      onSuccess: () => {
        notify('Spot selected', 'success');
        trackYardTaskUpdatedDestinationSpot(props.task, {
          additionalPayload: {
            destination_spot_id: selectedSpot.id
          }
        });
      },
      onError: error => {
        notify('Failed to select spot', 'error');
        console.error('Failed to select spot', error);
      }
    }
  );
};

const openSelectSpotDialog = () => {
  selectSpotDialog.triggerDialog({
    includeClosedSpots: 'disabled',
    includeAssignedSpots: 'disabled',
    includeReservedSpots: 'disabled',
    includeAssociatedToActiveMoveTasks: 'disabled',
    assetType: assetContainerType.value || undefined,
    onSpotSelected: handleSpotSelected
  });
};

const handleAssetSelected = (assetId: string, spotId: string) => {
  const taskId = props.task.id;
  if (!taskId) return;

  updateTaskMutation.mutate(
    {
      taskId,
      params: {
        child: {
          assetContainerId: assetId,
          originSpotId: spotId
        }
      }
    },
    {
      onSuccess: () => {
        notify('Asset selected', 'success');
        trackYardTaskUpdatedOriginSpot(props.task, {
          additionalPayload: {
            origin_spot_id: spotId,
            asset_container_id: assetId
          }
        });
      },
      onError: error => {
        notify('Failed to select asset', 'error');
        console.error('Failed to select asset', error);
      }
    }
  );
};

const openSelectAssetDialog = () => {
  selectAssetDialog.triggerDialog({
    includeAssociatedToActiveMoveTasks: 'disabled',
    includeAssignedAssets: true,
    onAssetSelected: handleAssetSelected
  });
};

const originClickProps = computed(() =>
  isOriginClickable.value ? { onClick: openSelectAssetDialog } : {}
);

const destinationClickProps = computed(() =>
  isDestinationClickable.value ? { onClick: openSelectSpotDialog } : {}
);

const handleIssueReported = () => {
  notify('Issue reported successfully', 'success');
};

const handleIssueCleared = () => {
  notify('Issue cleared successfully', 'success');
};

// TODO: this duplicates satellite's transition rules. `canCompleteYardTask` is stricter than the
// local rule (it also requires an asset container and a non-terminal status), so adopting it here
// would change when Complete is enabled — out of scope for OD-3958.
watch(
  [() => hasOriginSpot.value, () => hasDestinationSpot.value, () => isBlocked.value],
  ([origin, destination, blocked]) => {
    emit('allow-complete', origin && destination && !blocked);
  },
  { immediate: true }
);
</script>

<style lang="scss" scoped>
@use 'vuetify/settings' as v;
@use 'sass:map';

.v-card {
  border: 1px solid $color-line-border;

  &.outlined-border {
    border: 1px dashed $color-neutral-90 !important;
  }
}

.chevron-card {
  height: 138px;

  .v-card-text {
    height: 100%;
  }
}

.double-card {
  &.in-progress {
    .v-card {
      border: 1px solid $color-primary-60;
    }
  }
  .v-card {
    width: 50%;

    @media #{map.get(v.$display-breakpoints, 'sm-and-down')} {
      width: 100%;
    }
  }
}
</style>
